import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './Login';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Tabs from './components/Tabs';
import InstallWizard from './components/InstallWizard';
import ContractsStats from './modules/contracts/ContractsStats';
import ContractsView from './modules/contracts/ContractsView';
import NotificationsView from './modules/contracts/NotificationsView';
import AdminView from './modules/contracts/AdminView';
import OperationalView from './modules/operational/OperationalView';
import RebelnetStats from './modules/rebelnet/ContractsStats';
import RebelnetView from './modules/rebelnet/ContractsView';
import RebelnetConfigView from './modules/rebelnet/RebelnetConfigView';
import { useAuth } from './hooks/useAuth';
import { useWebSocket } from './hooks/useWebSocket';
import { useContracts } from './hooks/useContracts';
import { useRebelnet } from './hooks/useRebelnet';
import { useNotifications } from './hooks/useNotifications';
import { useOperational } from './hooks/useOperational';
import { useCountry } from './hooks/useCountry';
import { useTracking } from './hooks/useTracking';
import TrackingView from './modules/tracking/TrackingView';

function App() {
  const { isAuthenticated, user, isAdmin, getUserId, handleLoginSuccess, handleLogout } = useAuth();
  const { country, setCountry, SUPPORTED_COUNTRIES } = useCountry();

  const [view, setView] = useState(() => localStorage.getItem('currentView') || 'stats');
  const [subView, setSubView] = useState('stats'); // 'stats' or 'contracts'
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [selectedCategoria, setSelectedCategoria] = useState('all');
  const [selectedMandante, setSelectedMandante] = useState('all');
  const [rebelnetPeriod, setRebelnetPeriod] = useState('today');
  const [rebelnetCategoria, setRebelnetCategoria] = useState('all');
  const [rebelnetMandante, setRebelnetMandante] = useState('all');
  const [rebelnetSubView, setRebelnetSubView] = useState('stats');
  const [isPWA, setIsPWA] = useState(false);
  const [showInstallWizard, setShowInstallWizard] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [trackingPeriod, setTrackingPeriod] = useState('today');

  const { 
    contracts, stats, mandanti, categorie, lastUpdate,
    handleCategoriaChange, refresh 
  } = useContracts(selectedPeriod, selectedCategoria, selectedMandante, country);

  const { 
    contracts: rebelnetContracts, stats: rebelnetStats, 
    mandanti: rebelnetMandanti, categorie: rebelnetCategorie, 
    // eslint-disable-next-line no-unused-vars
    lastUpdate: rebelnetLastUpdate,
    handleCategoriaChange: handleRebelnetCategoriaChange, refresh: refreshRebelnet 
  } = useRebelnet(rebelnetPeriod, rebelnetCategoria, rebelnetMandante);

  const { 
    notificationsEnabled, isDeviceRegistered, preferences, countryPreferences, myDevices, subscriptions,
    requestNotificationPermission, registerPushSubscription,
    toggleMandantePreference, loadMyDevices, loadSubscriptions,
    toggleAllNotifications, deleteSubscription, setPreferences, setCountryPreferences
  } = useNotifications(getUserId, mandanti, rebelnetCategorie, country);

  const { notifications: operationalNotifications, refresh: refreshOperational } = useOperational(country);

  const { stats: trackingStats, events: trackingEvents, categories: trackingCategories, refresh: refreshTracking } = useTracking(country, trackingPeriod);

  useWebSocket((message) => {
    setTimeout(() => {
      refresh();
      refreshRebelnet();
      if (message.type === 'OPERATIONAL_UPDATE') {
        if (message.group === 'tracking') {
          refreshTracking();
        } else {
          refreshOperational();
        }
      }
    }, 500);
  }, country);

  // Map bottom nav to internal views
  const handleViewChange = (newView) => {
    setView(newView);
    // When switching to production or rebelnet, reset sub-view to stats
    if (newView === 'stats') setSubView('stats');
    if (newView === 'rebelnet-stats') setRebelnetSubView('stats');
  };

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    setIsPWA(isStandalone);
    
    if (!isStandalone) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) setShowInstallWizard(true);
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  useEffect(() => {
    if (view === 'settings') loadMyDevices();
    if (view === 'admin') loadSubscriptions();
  }, [view, loadMyDevices, loadSubscriptions]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    }
    setShowInstallWizard(false);
  };

  const handleRefresh = () => {
    refresh();
    refreshRebelnet();
    refreshOperational();
    refreshTracking();
    if (view === 'settings') loadMyDevices();
    if (view === 'admin') loadSubscriptions();
  };

  const handleCategoriaChangeWrapper = (categoria) => {
    setSelectedCategoria(categoria);
    setSelectedMandante('all');
    handleCategoriaChange(categoria);
  };

  const handleDeleteDevice = async (userId, deviceId, isCurrentDevice) => {
    await deleteSubscription(userId, deviceId);
    await loadMyDevices();
    if (isCurrentDevice) setPreferences([]);
  };

  // Determine which section is active for bottom nav
  const isProductionView = view === 'stats' || view === 'contracts';
  const isRebelnetView = view === 'rebelnet-stats' || view === 'rebelnet-contracts' || view === 'rebelnet-config';
  const isSettingsView = view === 'settings' || view === 'admin';

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      {showInstallWizard && (
        <InstallWizard 
          deferredPrompt={deferredPrompt}
          onInstall={handleInstallClick}
          onClose={() => setShowInstallWizard(false)}
        />
      )}

      <Header 
        user={user}
        isPWA={isPWA}
        notificationsEnabled={notificationsEnabled}
        lastUpdate={lastUpdate}
        onRefresh={handleRefresh}
        onLogout={handleLogout}
        country={country}
        onCountryChange={setCountry}
        countries={SUPPORTED_COUNTRIES}
      />

      {/* Production sub-tabs + filters */}
      {isProductionView && (
        <>
          <div className="sub-tabs">
            <button className={`sub-tab ${subView === 'stats' ? 'active' : ''}`} onClick={() => { setSubView('stats'); setView('stats'); }}>
              Statistiche
            </button>
            <button className={`sub-tab ${subView === 'contracts' ? 'active' : ''}`} onClick={() => { setSubView('contracts'); setView('contracts'); }}>
              Contratti
            </button>
          </div>
          <Tabs 
            selectedPeriod={selectedPeriod}
            selectedCategoria={selectedCategoria}
            selectedMandante={selectedMandante}
            categorie={categorie}
            mandanti={mandanti}
            onPeriodChange={setSelectedPeriod}
            onCategoriaChange={handleCategoriaChangeWrapper}
            onMandanteChange={setSelectedMandante}
          />
        </>
      )}

      {/* Rebelnet sub-tabs + filters */}
      {isRebelnetView && view !== 'rebelnet-config' && (
        <>
          <div className="sub-tabs">
            <button className={`sub-tab ${rebelnetSubView === 'stats' ? 'active' : ''}`} onClick={() => { setRebelnetSubView('stats'); setView('rebelnet-stats'); }}>
              Statistiche
            </button>
            <button className={`sub-tab ${rebelnetSubView === 'contracts' ? 'active' : ''}`} onClick={() => { setRebelnetSubView('contracts'); setView('rebelnet-contracts'); }}>
              Contratti
            </button>
            {isAdmin && (
              <button className={`sub-tab ${view === 'rebelnet-config' ? 'active' : ''}`} onClick={() => setView('rebelnet-config')}>
                Config
              </button>
            )}
          </div>
          <Tabs 
            selectedPeriod={rebelnetPeriod}
            selectedCategoria={rebelnetCategoria}
            selectedMandante={rebelnetMandante}
            categorie={rebelnetCategorie}
            mandanti={rebelnetMandanti}
            onPeriodChange={setRebelnetPeriod}
            onCategoriaChange={(cat) => {
              setRebelnetCategoria(cat);
              setRebelnetMandante('all');
              handleRebelnetCategoriaChange(cat);
            }}
            onMandanteChange={setRebelnetMandante}
          />
        </>
      )}

      {/* Settings sub-tabs */}
      {isSettingsView && isAdmin && (
        <div className="sub-tabs">
          <button className={`sub-tab ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}>
            Notifiche
          </button>
          <button className={`sub-tab ${view === 'admin' ? 'active' : ''}`} onClick={() => setView('admin')}>
            Gestione Utenti
          </button>
        </div>
      )}

      <main>
        {view === 'stats' && <ContractsStats stats={stats} selectedPeriod={selectedPeriod} />}
        {view === 'contracts' && <ContractsView contracts={contracts} />}
        {view === 'settings' && (
          <NotificationsView 
            notificationsEnabled={notificationsEnabled}
            isDeviceRegistered={isDeviceRegistered}
            preferences={preferences}
            mandanti={mandanti}
            myDevices={myDevices}
            operationalEnabled={preferences.includes('OPERATIONAL')}
            rebelnetCampaigns={rebelnetCategorie}
            onRequestPermission={requestNotificationPermission}
            onRegisterDevice={registerPushSubscription}
            onToggleMandante={toggleMandantePreference}
            onToggleOperational={() => toggleMandantePreference('OPERATIONAL')}
            onToggleRebelnetCampaign={toggleMandantePreference}
            onDeleteDevice={handleDeleteDevice}
            getUserId={getUserId}
            loadMyDevices={loadMyDevices}
            setPreferences={setPreferences}
            country={country}
            countryPreferences={countryPreferences}
            setCountryPreferences={setCountryPreferences}
            supportedCountries={SUPPORTED_COUNTRIES}
            trackingCategories={trackingCategories}
          />
        )}
        {view === 'admin' && isAdmin && (
          <AdminView 
            subscriptions={subscriptions}
            mandanti={mandanti}
            rebelnetCampaigns={rebelnetCategorie}
            onToggleAll={toggleAllNotifications}
            onDelete={deleteSubscription}
          />
        )}
        {view === 'operational' && <OperationalView notifications={operationalNotifications} />}
        {view === 'tracking' && (
          <TrackingView
            stats={trackingStats}
            events={trackingEvents}
            categories={trackingCategories}
            selectedPeriod={trackingPeriod}
            onPeriodChange={setTrackingPeriod}
          />
        )}
        {view === 'rebelnet-stats' && <RebelnetStats stats={rebelnetStats} selectedPeriod={rebelnetPeriod} />}
        {view === 'rebelnet-contracts' && <RebelnetView contracts={rebelnetContracts} />}
        {view === 'rebelnet-config' && isAdmin && <RebelnetConfigView />}
      </main>

      <Navigation 
        view={view}
        isAdmin={isAdmin}
        onViewChange={handleViewChange}
        country={country}
      />
    </div>
  );
}

export default App;
